import { isAxiosError } from 'axios';

import { getEibHeaders } from '~/shared/api/eibHeaders';
import { eibHubApi } from '~/shared/services/api';

import {
  mapRequestApiToFrontend,
  mapRequestDetailApiToFrontend,
  Request,
  RequestApiResponse,
  RequestCompensationDetailApiResponse,
  RequestPagination,
  RequestUpdateApiResponse
} from '../../MyRequests/types';

const eibHubApiRoute =
  process.env.NEXT_PUBLIC_EIB_HUB_API_ROUTE || '/api/eib-hub';

export class RequestsApiError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'RequestsApiError';
  }
}

class ApprovalsApi {
  constructor() {}

  async getRequests({
    page = 1,
    limit = 7
  }: { page?: number; limit?: number } = {}): Promise<RequestPagination> {
    try {
      const { data } = await eibHubApi.get(
        `${eibHubApiRoute}/request/approvals?page=${page}&limit=${limit}`,
        { headers: getEibHeaders() }
      );

      // Map API response to frontend model
      const mappedData = {
        ...data,
        data: data.data.map((item: RequestApiResponse) =>
          mapRequestApiToFrontend(item)
        )
      };

      return mappedData;
    } catch (error) {
      if (error instanceof RequestsApiError) {
        throw error;
      }
      throw new RequestsApiError(
        `Failed to fetch requests: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        isAxiosError(error) ? error.response?.status : undefined
      );
    }
  }

  async getRequestById(id: string): Promise<Request | null> {
    try {
      const { data } = await eibHubApi.get(
        `${eibHubApiRoute}/request/approvals/${id}`,
        { headers: getEibHeaders() }
      );

      // Map API response to frontend model
      const mappedRequest: Request = mapRequestApiToFrontend(data);

      // Map the request details (items) to RequestDetail[]
      if (data.items && Array.isArray(data.items)) {
        mappedRequest.details = data.items.map(
          (item: RequestCompensationDetailApiResponse) =>
            mapRequestDetailApiToFrontend(item)
        );
      }

      return mappedRequest;
    } catch (error) {
      if (error instanceof RequestsApiError) {
        throw error;
      }
      throw new RequestsApiError(
        `Failed to fetch request ${id}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  async requestUpdate(
    id: string,
    body: object
  ): Promise<RequestUpdateApiResponse> {
    try {
      const response = await eibHubApi.post(
        `${eibHubApiRoute}/request/${id}/spoc/request-update`,
        { ...body },
        { headers: getEibHeaders() }
      );

      return response?.data || response;
    } catch (error) {
      if (error instanceof RequestsApiError) {
        throw error;
      }
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      throw new RequestsApiError(
        `Failed updating request ${id}: ${errorMessage}`
      );
    }
  }

  async reject(id: string, body: object): Promise<RequestUpdateApiResponse> {
    try {
      const response = await eibHubApi.post(
        `${eibHubApiRoute}/request/${id}/spoc/reject`,
        { ...body },
        { headers: getEibHeaders() }
      );

      return response?.data || response;
    } catch (error) {
      if (error instanceof RequestsApiError) {
        throw error;
      }
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      throw new RequestsApiError(
        `Failed rejecting request ${id}: ${errorMessage}`
      );
    }
  }

  async approve(id: string): Promise<RequestUpdateApiResponse> {
    try {
      const response = await eibHubApi.post(
        `${eibHubApiRoute}/request/${id}/spoc/approve`,
        {},
        { headers: getEibHeaders() }
      );

      return response?.data || response;
    } catch (error) {
      if (error instanceof RequestsApiError) {
        throw error;
      }
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      throw new RequestsApiError(
        `Failed approving request ${id}: ${errorMessage}`
      );
    }
  }
}

export const approvalsApi = new ApprovalsApi();
